import React from "react";
import { Box, Group, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import styles from "./SettingsField.module.css";

export interface SettingsFieldProps {
  /** Field label (left column). */
  label: React.ReactNode;
  /** Optional help text shown in an info tooltip beside the label. */
  info?: React.ReactNode;
  /** When true, render `editControl` instead of the read value. */
  editing?: boolean;
  /** Read-mode value (right column). */
  children?: React.ReactNode;
  /** Edit-mode control (right column) — an input / select / switch. */
  editControl?: React.ReactNode;
  /** Mark the field required — renders an asterisk after the label. */
  required?: boolean;
  /** Draw the hairline bottom divider. Defaults to true. */
  withDivider?: boolean;
  /** Escape hatch. */
  className?: string;
}

/**
 * A single settings field row (design handoff §3.7): the label on the left and,
 * on the right, either the read value (weight 500) or — when `editing` — an
 * inline edit control in the same row. This is the default read/edit unit of My
 * Business. On narrow screens the row stacks (control drops full-width beneath
 * the label) so inputs stay usable on touch.
 */
export default function SettingsField({
  label,
  info,
  editing = false,
  children,
  editControl,
  required = false,
  withDivider = true,
  className,
}: SettingsFieldProps) {
  return (
    <Box
      className={[
        styles.row,
        editing && styles.editing,
        withDivider && styles.divider,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Group gap={6} wrap="nowrap" className={styles.labelBlock}>
        <Text component="span" className={styles.label}>
          {label}
          {required && (
            <Text
              component="span"
              className={styles.required}
              aria-hidden="true"
            >
              {" *"}
            </Text>
          )}
        </Text>
        {info && (
          <Tooltip
            label={info}
            multiline
            w={260}
            withArrow
            events={{ hover: true, focus: true, touch: true }}
          >
            <IconInfoCircle
              size="1rem"
              className={styles.infoIcon}
              aria-hidden
            />
          </Tooltip>
        )}
      </Group>
      <Box className={styles.value}>{editing ? editControl : children}</Box>
    </Box>
  );
}
